// RFC 3161 timestamp requests and responses. The Worker asks a TSA to timestamp each hourly
// Merkle root; anyone can later check the token with `openssl ts -verify`.
import { children, integer, oid, oidToString, readTlv, tlv } from "./der.ts";
import { fromHex, toHex } from "./bytes.ts";

const SHA256_OID = "2.16.840.1.101.3.4.2.1";
const SIGNED_DATA_OID = "1.2.840.113549.1.7.2";
const TSTINFO_OID = "1.2.840.113549.1.9.16.1.4";

export function buildTimestampRequest(sha256Hex: string, nonce?: Uint8Array): Uint8Array {
  const imprint = tlv(0x30, tlv(0x30, oid(SHA256_OID), new Uint8Array([0x05, 0x00])), tlv(0x04, fromHex(sha256Hex)));
  const parts = [integer(new Uint8Array([1])), imprint];
  if (nonce) parts.push(integer(nonce));
  parts.push(new Uint8Array([0x01, 0x01, 0xff])); // certReq TRUE: embed the TSA certificate
  return tlv(0x30, ...parts);
}

export interface TimestampInfo {
  status: number;
  hashAlgorithm: string;
  hashedMessage: string;
  serial: string;
  genTime: string; // ISO 8601
  policy: string;
  token: Uint8Array; // the TimeStampToken (ContentInfo) DER
}

function genTimeToIso(s: string): string {
  // YYYYMMDDHHMMSS[.fff]Z
  const m = s.match(/^(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})(\.\d+)?Z$/);
  if (!m) throw new Error(`unexpected GeneralizedTime ${s}`);
  return `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6]}${m[7] ?? ""}Z`;
}

/** Parses a TimeStampResp (or a bare TimeStampToken when `tokenOnly`). Does not verify the CMS signature. */
export function parseTimestampResponse(der: Uint8Array, tokenOnly = false): TimestampInfo {
  let status = 0;
  let token: Uint8Array;
  if (tokenOnly) {
    token = der;
  } else {
    const resp = children(readTlv(der));
    status = children(resp[0]!)[0]!.value[0]!;
    if (status > 1) throw new Error(`TSA refused the request (status ${status})`);
    if (!resp[1]) throw new Error("TSA response carries no token");
    token = resp[1].raw;
  }
  const ci = children(readTlv(token));
  if (oidToString(ci[0]!.value) !== SIGNED_DATA_OID) throw new Error("token is not SignedData");
  const signedData = children(children(ci[1]!)[0]!);
  const encap = children(signedData[2]!);
  if (oidToString(encap[0]!.value) !== TSTINFO_OID) throw new Error("token does not carry TSTInfo");
  const tstDer = children(encap[1]!)[0]!.value; // [0] EXPLICIT OCTET STRING
  const tst = children(readTlv(tstDer));
  const imprint = children(tst[2]!);
  return {
    status,
    policy: oidToString(tst[1]!.value),
    hashAlgorithm: oidToString(children(imprint[0]!)[0]!.value),
    hashedMessage: toHex(imprint[1]!.value),
    serial: toHex(tst[3]!.value),
    genTime: genTimeToIso(new TextDecoder().decode(tst[4]!.value)),
    token: new Uint8Array(token),
  };
}

export const TSA_ENDPOINTS = {
  sigstore: "https://timestamp.sigstore.dev/api/v1/timestamp",
  freetsa: "https://freetsa.org/tsr",
} as const;

export async function requestTimestamp(url: string, sha256Hex: string, fetchImpl: typeof fetch = fetch): Promise<TimestampInfo> {
  const nonce = crypto.getRandomValues(new Uint8Array(8));
  const res = await fetchImpl(url, {
    method: "POST",
    headers: { "Content-Type": "application/timestamp-query", Accept: "application/timestamp-reply" },
    body: buildTimestampRequest(sha256Hex, nonce) as BufferSource,
  });
  if (!res.ok) throw new Error(`TSA ${url} answered ${res.status}`);
  const info = parseTimestampResponse(new Uint8Array(await res.arrayBuffer()));
  if (info.hashedMessage !== sha256Hex || info.hashAlgorithm !== SHA256_OID) throw new Error("TSA timestamped a different digest");
  return info;
}

/** OpenTimestamps: submit a digest to a calendar; the reply is an incomplete .ots body to upgrade later. */
export async function submitOpenTimestamps(sha256Hex: string, calendar = "https://a.pool.opentimestamps.org", fetchImpl: typeof fetch = fetch): Promise<Uint8Array> {
  const res = await fetchImpl(`${calendar}/digest`, {
    method: "POST",
    headers: { Accept: "application/vnd.opentimestamps.v1", "Content-Type": "application/x-www-form-urlencoded" },
    body: fromHex(sha256Hex) as BufferSource,
  });
  if (!res.ok) throw new Error(`OpenTimestamps calendar answered ${res.status}`);
  return new Uint8Array(await res.arrayBuffer());
}

/** Wraps a calendar reply into a standalone .ots file (header, version, op sha256, digest, reply). */
export function otsFile(sha256Hex: string, calendarReply: Uint8Array): Uint8Array {
  const magic = new Uint8Array([0x00, 0x4f, 0x70, 0x65, 0x6e, 0x54, 0x69, 0x6d, 0x65, 0x73, 0x74, 0x61, 0x6d, 0x70, 0x73, 0x00, 0x00, 0x50, 0x72, 0x6f, 0x6f, 0x66, 0x00, 0xbf, 0x89, 0xe2, 0xe8, 0x84, 0xe8, 0x92, 0x94]);
  const d = fromHex(sha256Hex);
  const out = new Uint8Array(magic.length + 2 + d.length + calendarReply.length);
  out.set(magic, 0);
  out[magic.length] = 0x01; // file format version
  out[magic.length + 1] = 0x08; // file hash op: SHA256
  out.set(d, magic.length + 2);
  out.set(calendarReply, magic.length + 2 + d.length);
  return out;
}
